import { useEffect } from "react";

import {
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_ALT,
  SITE_URL,
} from "../seo/publicPages";

/**
 * Per-route SEO metadata in the live SPA (after JavaScript).
 *
 * Unique title/description/body for crawlers are written into each public
 * HTML file at build time (`prerenderPublicPages`). This component still
 * updates `<head>` on client navigations between those pages.
 */

type SeoProps = {
  /** Page title; rendered as-is in `<title>`. Include the brand suffix when relevant. */
  title: string;
  /** Meta description (~150-160 chars). */
  description: string;
  /** Absolute path on this site (e.g. `/`, `/termeni-si-conditii`). Used for canonical + `og:url`. */
  path: string;
  ogType?: "website" | "article";
  /** Hide this URL from search results (404). */
  noIndex?: boolean;
  /** Absolute URL to the social card image. Defaults to the site-wide `/og-image.jpg`. */
  ogImage?: string;
  ogImageAlt?: string;
};

function upsertMeta(args: {
  name?: string;
  property?: string;
  content: string;
}) {
  const { name, property, content } = args;
  const key = name ? "name" : "property";
  const value = name ?? property!;
  const selector = `meta[${key}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({
  title,
  description,
  path,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  noIndex = false,
}: SeoProps) {
  useEffect(() => {
    const url = `${SITE_URL}${path === "/" ? "/" : path}`;

    document.title = title;
    upsertMeta({ name: "description", content: description });

    if (noIndex) {
      upsertMeta({ name: "robots", content: "noindex, nofollow" });
      document.head.querySelector('link[rel="canonical"]')?.remove();
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove();
      upsertCanonical(url);
      upsertMeta({ property: "og:url", content: url });
    }

    upsertMeta({ property: "og:title", content: title });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:type", content: ogType });
    upsertMeta({ property: "og:image", content: ogImage });
    upsertMeta({ property: "og:image:alt", content: ogImageAlt });
    upsertMeta({ property: "og:image:width", content: "1200" });
    upsertMeta({ property: "og:image:height", content: "630" });

    upsertMeta({ name: "twitter:title", content: title });
    upsertMeta({ name: "twitter:description", content: description });
    upsertMeta({ name: "twitter:image", content: ogImage });
    upsertMeta({ name: "twitter:image:alt", content: ogImageAlt });
  }, [title, description, path, ogType, ogImage, ogImageAlt, noIndex]);

  return null;
}
